import { useCallback, useRef, useState } from "react";
import {
  VolumeHighIcon,
  VolumeLowIcon,
  VolumeMediumIcon,
  VolumeMutedIcon,
} from "../components/Icons";
import { useVideoPlayer2 } from "../hooks/use-video-player-2-setup-start";
import { useVideoPlayer2Volume } from "../hooks/use-video-player-helpers";

// Custom hook for volume slider logic
function useVolumeSlider({
  volume,
  setVolume,
  isMuted,
  sliderHeight,
}: {
  volume: number;
  setVolume: (v: number) => void;
  isMuted: boolean;
  sliderHeight: number;
}) {
  const sliderRef = useRef<HTMLDivElement>(null);

  // Calculate thumb position (bottom = 0 is min, bottom = sliderHeight is max)
  const thumbPos = (isMuted ? 0 : volume) * sliderHeight;

  // Helper to set volume from mouse event
  const setVolumeFromEvent = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (!sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const y = (e as MouseEvent).clientY - rect.top;
      // Clamp y between 0 and sliderHeight
      const clampedY = Math.max(0, Math.min(sliderHeight, y));
      // Invert: y=0 (top) => 1, y=sliderHeight (bottom) => 0
      const newVolume = 1 - clampedY / sliderHeight;

      setVolume(newVolume);
    },
    [setVolume, sliderHeight],
  );

  // Mouse/touch drag logic
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setVolumeFromEvent(e);
    const moveListener = (moveEvent: MouseEvent) => {
      setVolumeFromEvent(moveEvent);
    };
    const upListener = () => {
      window.removeEventListener("mousemove", moveListener);
      window.removeEventListener("mouseup", upListener);
    };
    window.addEventListener("mousemove", moveListener);
    window.addEventListener("mouseup", upListener);
  };

  const handleSliderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setVolumeFromEvent(e);
  };

  // Keyboard accessibility
  const handleSliderKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowRight") {
      setVolume(Math.min(1, volume + 0.05));
      e.preventDefault();
    } else if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
      setVolume(Math.max(0, volume - 0.05));
      e.preventDefault();
    }
  };

  return {
    sliderRef,
    thumbPos,
    handleThumbMouseDown,
    handleSliderMouseDown,
    handleSliderKeyDown,
  };
}

export function VolumeControl() {
  const player = useVideoPlayer2();
  const { volume, setVolume, isMuted, toggleMute, volumeLevel } =
    useVideoPlayer2Volume();

  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const SLIDER_HEIGHT = 80; // px, must match CSS

  // Use the custom hook for slider logic
  const {
    sliderRef,
    thumbPos,
    handleThumbMouseDown,
    handleSliderMouseDown,
    handleSliderKeyDown,
  } = useVolumeSlider({
    volume,
    setVolume,
    isMuted,
    sliderHeight: SLIDER_HEIGHT,
  });

  // Popup hover/focus handling
  const handleShow = () => setShowPopup(true);
  const handleHide = () => setShowPopup(false);

  return (
    <div
      className="video-player-2-volume"
      onMouseEnter={handleShow}
      onMouseLeave={handleHide}
      onFocus={handleShow}
      onBlur={handleHide}
      tabIndex={-1}
      style={{ position: "relative" }}>
      <div className="video-player-2-volume__wrapper">
        <button
          className="video-player-2-btn video-player-2-volume__icon"
          onClick={toggleMute}
          aria-label={isMuted ? player.trans("unmute") : player.trans("mute")}>
          {volumeLevel === "muted" && <VolumeMutedIcon />}
          {volumeLevel === "low" && <VolumeLowIcon />}
          {volumeLevel === "medium" && <VolumeMediumIcon />}
          {volumeLevel === "high" && <VolumeHighIcon />}
        </button>
        <div
          className={`video-player-2-volume__popup${
            showPopup ? " video-player-2-volume__popup--visible" : ""
          }`}
          ref={popupRef}>
          <div
            className="video-player-2-volume__slider-custom"
            ref={sliderRef}
            tabIndex={0}
            role="slider"
            aria-valuenow={isMuted ? 0 : Math.round(volume * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-orientation="vertical"
            aria-label="Volume"
            onMouseDown={handleSliderMouseDown}
            onKeyDown={handleSliderKeyDown}
            style={{
              height: SLIDER_HEIGHT,
              width: 28,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <div
              className="video-player-2-volume__slider-track"
              style={{
                width: 6,
                height: SLIDER_HEIGHT,
                background: "#888",
                borderRadius: 3,
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            />
            <div
              className="video-player-2-volume__slider-fill"
              style={{
                width: 6,
                height: (isMuted ? 0 : volume) * SLIDER_HEIGHT,
                background: "#fff",
                borderRadius: 3,
                position: "absolute",
                left: "50%",
                bottom: 0,
                transform: "translateX(-50%)",
              }}
            />
            <div
              className="video-player-2-volume__slider-thumb"
              style={{
                width: 18,
                height: 8,
                borderRadius: 4,
                background: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                position: "absolute",
                left: "50%",
                transform: `translate(-50%, 0)`,
                bottom: thumbPos,
                cursor: "pointer",
                border: "none",
                transition: "background 0.2s",
                zIndex: 2,
              }}
              tabIndex={-1}
              onMouseDown={handleThumbMouseDown}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
